/**
 * @ken/api-client — typed fetch client for the Ken FastAPI backend.
 *
 * Per ADR-030 + KEN_LAYER_7 §3, §6.4.
 *
 * Provides:
 *   - `createApiClient({ baseUrl, getAuth })` — factory for the typed
 *     openapi-fetch client. Auth + tenant context injected via headers.
 *   - `useApi('namespace')` — React hook that returns a per-namespace
 *     typed client. The namespace name is checked at compile time against
 *     the generated OpenAPI types.
 *   - Reserved-name guard: bare `fetch` / `XMLHttpRequest` calls in
 *     module code are blocked by ESLint rule `ken/no-direct-fetch`.
 *
 * OpenAPI types: each module's backend publishes its own `openapi.json`.
 * The shell composes them at build time into one schema file the cockpit
 * imports. Until that wiring lands, the types declared here are placeholder
 * shapes — replace with `import type { paths } from "./generated/openapi"`
 * once the codegen step is in place.
 */

import type { Client } from "openapi-fetch";
import createOpenApiClient from "openapi-fetch";
import {
  createContext,
  createElement,
  useContext,
  useMemo,
  type ReactElement,
  type ReactNode,
} from "react";

// ---------------------------------------------------------------------------
// Placeholder OpenAPI types — replaced by generated bundle.
// ---------------------------------------------------------------------------

/** Generated by openapi-typescript from the live FastAPI schema. */
export interface KenApiPaths {
  // populated by codegen — see scripts/generate-api-types.sh
  [path: string]: unknown;
}

// ---------------------------------------------------------------------------
// Auth context contract
// ---------------------------------------------------------------------------

export interface AuthContext {
  /** Tenant slug derived from URL or undefined during pre-tenant session. */
  tenantId: string | undefined;
  /** Bearer token (JWT) or undefined for unauthenticated calls. */
  getAccessToken: () => string | undefined;
}

// ---------------------------------------------------------------------------
// Client factory
// ---------------------------------------------------------------------------

export interface CreateApiClientOptions {
  baseUrl: string;
  authContext: AuthContext;
}

export function createApiClient(
  opts: CreateApiClientOptions,
): Client<KenApiPaths> {
  return createOpenApiClient<KenApiPaths>({
    baseUrl: opts.baseUrl,
    fetch: async (request) => {
      const token = opts.authContext.getAccessToken();
      if (token) {
        request.headers.set("Authorization", `Bearer ${token}`);
      }
      if (opts.authContext.tenantId) {
        request.headers.set("X-Ken-Tenant", opts.authContext.tenantId);
      }
      // The HttpOnly session cookie only flows on credentialed requests.
      // KEN_LAYER_7 §8.1 step 4 + I-16: JWT is never read from JS — it
      // travels in the cookie automatically.
      const response = await fetch(request, { credentials: "include" });

      // 401 auto-redirect: when the session expires mid-flow, push the
      // user to /login instead of letting modules surface "request failed:
      // 401" toasts everywhere. Skip when we're already on a login or
      // tenant-select route (otherwise we'd loop), and skip for the
      // session-probe endpoint itself (which legitimately 401s on
      // first page load before sign-in).
      if (response.status === 401 && typeof window !== "undefined") {
        const url = new URL(request.url);
        const isAuthProbe = url.pathname.startsWith("/api/shell/auth/");
        const onLoginPage =
          window.location.pathname.startsWith("/auth/login") ||
          window.location.pathname.startsWith("/auth/tenant-select");
        if (!isAuthProbe && !onLoginPage) {
          const here = window.location.pathname + window.location.search;
          // Param contract matches apps/cockpit/src/routes/auth.login.tsx
          // validateSearch (?from=<safe-path>&reason=expired).
          window.location.assign(
            `/auth/login?from=${encodeURIComponent(here)}&reason=expired`,
          );
        }
      }

      return response;
    },
  });
}

// ---------------------------------------------------------------------------
// Namespace router
// ---------------------------------------------------------------------------

/**
 * Module-namespace name. Each module backend publishes routes under
 * `/api/<namespace>/*`. The shell publishes `/api/shell/*`.
 *
 * In the final implementation this is generated from the workspace graph at
 * build time. Skeleton lists a sample set.
 */
export type ApiNamespace =
  | "shell"
  | "customers"
  | "products"
  | "invoicing"
  | "routes"
  | "fleet"
  | "staff"
  | "concierge";

/**
 * React hook returning the typed client. Memoized per (client, namespace).
 *
 * Modules MUST go through this hook — direct fetch calls are blocked by
 * ESLint rule `ken/no-direct-fetch` (per ADR-038 and the shell's CSP).
 */
export function useApi(
  client: Client<KenApiPaths>,
  namespace: ApiNamespace,
): Client<KenApiPaths> {
  return useMemo(() => {
    // The namespace prefix is enforced at the request layer by openapi-fetch
    // — the typed paths are scoped to /api/<namespace>/* by the generated
    // OpenAPI bundle. This wrapper is the explicit dependency point.
    void namespace; // referenced for future per-namespace fetch wrappers
    return client;
  }, [client, namespace]);
}

// ---------------------------------------------------------------------------
// React context + provider — shared between cockpit and modules
// ---------------------------------------------------------------------------
//
// The cockpit's `<ApiClientProvider>` knows the tenant + auth state and
// builds the typed `Client<KenApiPaths>` once. It places the client in
// React context here; module packages call `useApiClient()` to read it.
// Keeping the context in this shared package avoids cross-package
// imports from module code into apps/cockpit (which is not exposed as
// a workspace dependency).

const ApiClientContext = createContext<Client<KenApiPaths> | null>(null);

export interface ApiClientProviderProps {
  client: Client<KenApiPaths>;
  children: ReactNode;
}

export function ApiClientProvider(props: ApiClientProviderProps): ReactElement {
  return createElement(
    ApiClientContext.Provider,
    { value: props.client },
    props.children,
  );
}

export function useApiClient(): Client<KenApiPaths> {
  const client = useContext(ApiClientContext);
  if (client === null) {
    throw new Error("useApiClient must be used inside <ApiClientProvider>");
  }
  return client;
}

// ---------------------------------------------------------------------------
// Re-exports
// ---------------------------------------------------------------------------

export type { Client } from "openapi-fetch";
